import { styled } from '@mui/material/styles';
import Button from '@/components/Button/Button';
import FilterIcon from '@/assets/filter.svg?react';
import { ReactNode, useRef, useState } from 'react';
import Popper from '@/components/Popper/Popper';
import { flattenDeep } from 'lodash';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import UploadIcon from '@/assets/upload.svg?react';
import { FormattedMessage } from 'react-intl';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';

const TableActions = ({
  path,
  onPathChange,
  filters,
  filtersValues,
  actions,
  onUploadClick,
  showPath = true,
}: {
  path?: string[];
  onPathChange?: (path: string[]) => void;
  filters?: ReactNode;
  filtersValues?: Partial<FilterSchema>;
  actions?: ReactNode[];
  onUploadClick?: () => void;
  showPath?: boolean;
}) => {
  const filterRef = useRef<HTMLButtonElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const showNotification =
    flattenDeep<
      | string
      | number
      | boolean
      | string[]
      | number[]
      | [string, string]
    >(Object.values(filtersValues || {})).length > 0;

  const breadcrumbPath = path?.slice(1) ?? [];

  return (
    <Root>
      <Path>
        {showPath && onPathChange ? (
          <Breadcrumbs
            path={breadcrumbPath}
            onPathChange={onPathChange}
          />
        ) : null}
      </Path>
      <TableActionButtons>
        {actions?.map((action) => action)}
        {onUploadClick ? (
          <Button
            onClick={onUploadClick}
            variant="contained"
            aria-label="Upload Button"
          >
            <UploadIcon />
            <FormattedMessage id="upload" defaultMessage="Upload" />
          </Button>
        ) : null}
        {filters ? (
          <Button
            variant="outlined"
            icon
            ref={filterRef}
            onClick={() => setFiltersOpen((v) => !v)}
            aria-label="Filter Button"
          >
            <FilterIcon />
            {showNotification && <FilterNotification />}
          </Button>
        ) : null}
        {filters ? (
          <Popper
            aria-label="Filters Popper"
            open={filtersOpen}
            anchorEl={filterRef.current}
            onClose={() => setFiltersOpen(false)}
          >
            {filters}
          </Popper>
        ) : null}
      </TableActionButtons>
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 40px',
  overflow: 'hidden',
});

const Path = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const TableActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexGrow: 1,
  justifyContent: 'flex-end',
}));

const FilterNotification = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  position: 'absolute',
  top: -4,
  right: -4,
}));

export default TableActions;
